import React from 'react';
import PropTypes from 'prop-types';
import { JsonLdScript } from 'components/atoms';
import { getUnitIconSrc } from 'components/unit/UnitIconV2/UnitIconV2';

const PRODUCTION_BASE_URL = 'https://www.selfstorage.com';

// These unit types resolve to a fixed icon regardless of squareFootage.
// Keep in sync with the fixed-icon cases in getUnitIconSrc's switch (UnitIconV2.js) — a new fixed-icon type there needs to be added here too, or it'll silently emit no image.
const SQFT_INDEPENDENT_TYPES = [
	'locker',
	'outdoor',
	'workspace',
	'wine_storage',
];

const createSizeSlug = (size) => {
	if (!size) return 'unit';
	// Convert "8' x 20'" to "8x20"
	return size.replaceAll(/['\s]/g, '').toLowerCase();
};

const buildUnitSchema = (unit, facility, baseUrl) => {
	const rootUrl = baseUrl || PRODUCTION_BASE_URL;
	const size = unit.sizeText || unit.size || 'Storage Unit';
	const sizeSlug = createSizeSlug(size);
	const amenities = unit.availableAmenities?.length
		? unit.availableAmenities.join(', ')
		: 'secure storage';
	const price =
		unit.price?.sparefoot || unit.price?.regular || unit.price?.meta;
	const facilityUrl = `${rootUrl}${facility?.url || ''}`;
	const iconSrc =
		SQFT_INDEPENDENT_TYPES.includes(unit.unitType) || unit.squareFootage
			? getUnitIconSrc({
					type: unit.unitType,
					squareFootage: unit.squareFootage,
					amenities: unit.amenities,
				})
			: undefined;

	return {
		'@context': 'https://schema.org',
		'@type': 'Product',
		name: `${size} Self Storage Unit at ${facility?.name || 'this facility'}`,
		// Only absolute or root-relative srcs are usable as JSON-LD image; data URIs are dropped.
		...(iconSrc?.startsWith('/') && { image: `${rootUrl}${iconSrc}` }),
		...(iconSrc?.startsWith('http') && { image: iconSrc }),
		description: `${size} self storage unit with ${amenities}. Reserve online safely and conveniently at SelfStorage.com.`,
		brand: {
			'@type': 'Brand',
			name: facility?.name || 'SelfStorage.com',
		},
		offers: {
			'@type': 'Offer',
			priceCurrency: 'USD',
			...(price != null && { price: price.toString() }),
			availability:
				unit.quantity === 0
					? 'https://schema.org/OutOfStock'
					: 'https://schema.org/InStock',
			url: `${facilityUrl}#${sizeSlug}`,
			inventoryLevel: {
				'@type': 'QuantitativeValue',
				value: unit.quantity ?? 1,
			},
		},
	};
};

export function UnitProductSchema({ units, facility, baseUrl }) {
	if (!units?.length || !facility) return null;

	const schemas = units
		.filter(Boolean)
		.map((unit) => buildUnitSchema(unit, facility, baseUrl));

	return (
		<>
			{schemas.map((schema) => (
				<JsonLdScript
					key={schema.offers?.url || schema.name}
					schema={schema}
				/>
			))}
		</>
	);
}

UnitProductSchema.propTypes = {
	units: PropTypes.arrayOf(PropTypes.object),
	facility: PropTypes.object.isRequired,
	baseUrl: PropTypes.string,
};

export default UnitProductSchema;
